import { useAppSelector } from '../../../../app/store/hooks/Redux'
import { ILanguageComplianceFilter } from '../../model/types/LanguageComplianceFilter.interface'
import {
  selectLanguageComplianceFilter,
  selectLanguageCompliancePagination,
  setLanguageCompliancePagination,
} from '../../model/languageComplianceSlice'
import React, { useEffect, useState } from 'react'
import {
  useGetLanguageComplianceInProgressQuery,
  usePostLanguageComplianceUpdateStateMutation,
} from '../../../../app/api'
import { ColumnsType } from 'antd/lib/table'
import { Space } from 'antd'
import { useDispatch } from 'react-redux'
import { LanguageComplianceTableExpandRow } from '../language-compliance-table-expand-row/LanguageComplianceTableExpandRow'
import { IPagination } from '../../../../shared/types/Pagination.interface'
import { PageCell } from '../../ui/cells/PageCell'
import { AffiliateCell } from '../../ui/cells/AffiliateCell'
import { LastCheckCell } from '../../ui/cells/LastCheckCell'
import { ResolveButtonCell } from '../../ui/cells/ResolveButtonCell'
import { HiddenActionCell } from '../../ui/cells/HiddenActionCell'
import clsx from 'clsx'
import styles from './InProgressTable.module.scss'
import { Table } from '../../../../lib/ui/table/components/table/Table'
import { IconArrowRight } from '../../../../shared/components/icons/IconArrowRight'
import { IconEye } from '../../../../shared/components/icons/IconEye'
import { IconEyeOff } from '../../../../shared/components/icons/IconEyeOff'
import { useTranslation } from 'react-i18next'

export const InProgressTable = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const filters = useAppSelector<ILanguageComplianceFilter>(selectLanguageComplianceFilter)
  const { current, pageSize } = useAppSelector<IPagination>(selectLanguageCompliancePagination)
  const { project, country, url, affiliate, dateRange, restrictedWords, hidden } = filters

  const [selectedRows, setSelectedRows] = useState<any[]>([])
  const [queryParams, setQueryParams] = useState<any>({
    page: current,
    'per-page': pageSize,
  })
  const { data, isLoading, isFetching, refetch } = useGetLanguageComplianceInProgressQuery(
    queryParams,
    {
      skip: !queryParams.projectId || !queryParams.countryId,
      refetchOnMountOrArgChange: true,
    },
  )
  const [updateState] = usePostLanguageComplianceUpdateStateMutation()

  useEffect(() => {
    setQueryParams({
      ...queryParams,
      date: dateRange,
      projectId: Number(project),
      countryId: country,
      urlId: url || undefined,
      affiliate: affiliate,
      wordId: restrictedWords || undefined,
      showHidden: hidden,
    })
  }, [filters])

  const columns: ColumnsType<any> = [
    {
      title: 'Page',
      dataIndex: 'url',
      key: 'url',
      render: (_: string, record) => <PageCell {...record} />,
    },
    {
      title: 'Affiliate',
      dataIndex: 'affiliate',
      key: 'affiliate',
      render: (_: string, record) => <AffiliateCell {...record} />,
    },
    {
      title: 'Checks',
      dataIndex: 'check_count',
      key: 'check_count',
    },
    {
      title: 'Last Check',
      dataIndex: 'last_check',
      key: 'last_check',
      width: 200,
      sorter: true,
      render: (_: string, record) => <LastCheckCell {...record} />,
    },
    {
      key: 'actions',
      render: (_: string, record) => (
        <Space>
          <ResolveButtonCell
            {...record}
            country={country}
            project={project}
            onResolve={() => {
              refetch()
            }}
          />
          <HiddenActionCell
            {...record}
            country={country}
            project={project}
            onToggle={() => {
              refetch()
            }}
          />
        </Space>
      ),
      width: 144,
      fixed: 'right',
    },
  ]

  const Toolbar = () => {
    return (
      <div className={'flex items-center justify-between'}>
        <div className={'flex items-center gap-[24px]'}>
          <p className={'font-medium text-[#6684A7]'}>
            {t('other.countSelected', { count: selectedRows.length })}
          </p>
          <div className={'flex items-center gap-[24px]'}>
            <button
              className={'flex items-center gap-[6px] text-[#A059FF]'}
              onClick={() => {
                updateState({
                  stateForm: {
                    project_id: Number(project),
                    country_id: country,
                    urls: selectedRows.map((row) => row.url),
                    state: 'resolved',
                  },
                }).then(() => {
                  refetch().then(() => {
                    setSelectedRows([])
                  })
                })
              }}
            >
              <div className={'w-[11px]'}>
                <IconArrowRight color={'#A059FF'} />
              </div>
              {t('buttons.resolve')}
            </button>
            {selectedRows.find((row) => row.is_hidden) && (
              <button
                className={'flex items-center gap-[6px] text-[#A059FF]'}
                onClick={() => {
                  updateState({
                    stateForm: {
                      project_id: Number(project),
                      country_id: country,
                      urls: selectedRows.map((row) => row.url),
                      is_hidden: 0,
                    },
                  }).then(() => {
                    refetch().then(() => {
                      setSelectedRows([])
                    })
                  })
                }}
              >
                <div className={'w-[13px]'}>
                  <IconEye color={'#A059FF'} />
                </div>
                {t('buttons.unhide')}
              </button>
            )}
            {selectedRows.find((row) => !row.is_hidden) && (
              <button
                className={'flex items-center gap-[6px] text-[#A059FF]'}
                onClick={() => {
                  updateState({
                    stateForm: {
                      project_id: Number(project),
                      country_id: country,
                      urls: selectedRows.map((row) => row.url),
                      is_hidden: 1,
                    },
                  }).then(() => {
                    refetch().then(() => {
                      setSelectedRows([])
                    })
                  })
                }}
              >
                <div className={'w-[13px]'}>
                  <IconEyeOff color={'#A059FF'} />
                </div>
                {t('buttons.hide')}
              </button>
            )}
          </div>
        </div>
        <button className={'text-[#DC2F02]'} onClick={() => setSelectedRows([])}>
          {t('buttons.clearSelection')}
        </button>
      </div>
    )
  }

  return (
    <div className={clsx(styles.root)}>
      <Table
        showExpandIcon
        columns={columns}
        data={data}
        expandedRowRender={(record: any) => <LanguageComplianceTableExpandRow record={record} />}
        loading={isLoading || isFetching}
        rowClassName={(e) => (e.is_hidden ? 'disabled' : '')}
        rowKey={(record) => record.url}
        selectedRowKeys={selectedRows}
        toolbar={selectedRows.length ? <Toolbar /> : null}
        onPaginationChange={(page, pageSize, total) => {
          dispatch(
            setLanguageCompliancePagination({
              current: page,
              pageSize: pageSize,
              total: total,
            }),
          )
          setQueryParams({
            ...queryParams,
            page: page,
            'per-page': pageSize,
          })
        }}
        onRowSelectionChange={(selection) => {
          setSelectedRows(selection)
        }}
        onSortingChange={(sorter) => {
          setQueryParams({
            ...queryParams,
            sort: sorter,
          })
        }}
      />
    </div>
  )
}
